import { useQuery } from '@apollo/client'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import {
  SearchAutoCompleteDocument,
  SearchAutoCompleteQuery,
  SearchExactParentOrChildSkuDocument,
} from '../../../graphql/search'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { useRefetchOnChange } from '../../../hooks/useRefetchOnChange'
import { useSearchModalContent } from '../../../hooks/useSearchModalContent'
import { TokenContext } from '../../../layouts/context'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { useSearchLazyQuery } from '../../../lib/search'
import { isNotNull } from '../../../utils/collectionTools'
import { SearchDropdown, SearchDropdownProps } from './SearchDropdown'

export interface SearchContainerProps {
  closeSearch: () => void
  showSearch: boolean
  inputValue: string
}

const pushAnalyticsEvents: (data: SearchAutoCompleteQuery) => void = (data) => {
  const { query, totalCount } = data.autoComplete
  if (totalCount === 0) {
    setAnalyticsEvent({
      event: 'search_no_results',
      search_term: query,
    })
  } else {
    setAnalyticsEvent({
      event: 'search',
      search_term: query,
      result_count: totalCount,
    })
  }
}

export const SearchContainer = React.memo(function SearchContainer({
  closeSearch,
  showSearch = false,
  inputValue,
}: SearchContainerProps) {
  const keywords = (inputValue || '').trim()

  const { sanitySearchModalContent } = useSearchModalContent() ?? {}
  const [executeAutoCompleteSearch, { data, loading, error, refetch, called }] = useSearchLazyQuery(
    SearchAutoCompleteDocument,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: pushAnalyticsEvents,
    },
  )
  const token = useContext(TokenContext)
  const defaultProductSkus = useMemo(
    () =>
      sanitySearchModalContent?.products
        ?.map((product) => product?.sku)
        .filter((sku): sku is string => !!sku) ?? [],
    [sanitySearchModalContent?.products],
  )

  const {
    data: defaultProductsData,
    loading: defaultProductsLoading,
    refetch: defaultProductsRefetch,
    networkStatus,
  } = useQuery(SearchExactParentOrChildSkuDocument, {
    context: {
      token,
      uri: process.env.GATSBY_SEARCH_URL,
    },
    variables: {
      sku: defaultProductSkus,
    },
    notifyOnNetworkStatusChange: true,
    skip: defaultProductSkus.length === 0,
  })

  useRefetchOnChange({ networkStatus, refetch: defaultProductsRefetch }, [token])
  useLogAndCaptureError(error)

  const sanitySearchTerms = useMemo(
    () => sanitySearchModalContent?.searchTerms?.filter(isNotNull) ?? [],
    [sanitySearchModalContent?.searchTerms],
  )

  const defaultState = useMemo(() => {
    const productsList = (defaultProductsData?.exact.results ?? []).filter(isNotNull)
    const products = {
      show: defaultProductsLoading ? defaultProductSkus.length > 0 : productsList.length > 0,
      list: productsList,
    }

    const searchTermsList = sanitySearchTerms
      .filter((term) => !!term.isDefault)
      .map((term) => term.term)
      .filter(isNotNull)
    const searchTerms = {
      show: searchTermsList.length > 0,
      list: searchTermsList,
    }

    const categoriesList = (sanitySearchModalContent?.categories ?? []).filter(isNotNull)
    const categories = {
      show: categoriesList.length > 0,
      list: categoriesList,
    }

    return {
      show: products.show || categories.show || searchTerms.show,
      products,
      searchTerms,
      categories,
    }
  }, [
    defaultProductSkus,
    defaultProductsLoading,
    defaultProductsData?.exact.results,
    sanitySearchModalContent?.categories,
    sanitySearchTerms,
  ])

  const [results, setResults] = useState<SearchAutoCompleteQuery['autoComplete']['results']>(
    data?.autoComplete?.results ?? [],
  )
  const [categoriesResults, setCategoriesResults] = useState<
    SearchAutoCompleteQuery['autoComplete']['aggregations']['category']
  >(data?.autoComplete?.aggregations.category)

  useEffect(() => {
    if (loading) {
      return
    }
    setResults(data?.autoComplete?.results ?? [])
    setCategoriesResults(data?.autoComplete?.aggregations.category ?? undefined)
  }, [data?.autoComplete?.aggregations.category, data?.autoComplete?.results, loading])

  useEffect(() => {
    if (!keywords) {
      return
    }
    const apiVariables = {
      query: keywords,
      limit: 15,
    }
    if (called && refetch) {
      refetch(apiVariables)
    } else {
      executeAutoCompleteSearch({ variables: apiVariables })
    }
  }, [called, executeAutoCompleteSearch, refetch, keywords])

  const shouldShow = keywords ? showSearch : showSearch && defaultState.show

  const products = useMemo((): SearchDropdownProps['products'] => {
    const showInvalid = !!keywords && results.length === 0 && !loading
    const show = !!keywords || defaultState.products.show
    const isLoading = keywords ? loading : defaultProductsLoading
    const title = keywords ? 'Products' : 'Popular Products'
    const list = keywords
      ? showInvalid
        ? defaultState.products.list
        : results
      : defaultState.products.list
    return { show, showInvalid, loading: isLoading, title, list }
  }, [defaultProductsLoading, defaultState.products, keywords, loading, results])

  const searchTerms = useMemo((): SearchDropdownProps['searchTerms'] => {
    const searchTermsMatched = keywords
      ? sanitySearchTerms
          .map((term) => term.term)
          .filter(
            (term): term is string =>
              !!term && term.toLowerCase().startsWith(keywords.toLowerCase()),
          )
      : []
    const show = keywords ? sanitySearchTerms.length > 0 : defaultState.searchTerms.show
    const list = keywords ? searchTermsMatched : defaultState.searchTerms.list
    return { show, list }
  }, [keywords, sanitySearchTerms, defaultState.searchTerms])

  const categories = useMemo((): SearchDropdownProps['categories'] => {
    const show = !!keywords || defaultState.categories.show
    const isLoading = keywords ? loading : false
    const list = keywords
      ? (categoriesResults?.options ?? []).map((category) => category.value).filter(isNotNull)
      : defaultState.categories.list
    return { show, loading: isLoading, list }
  }, [defaultState.categories, keywords, loading, categoriesResults?.options])

  if (!showSearch) {
    return null
  }

  return (
    <SearchDropdown
      shouldShow={shouldShow}
      closeSearch={closeSearch}
      keywords={keywords}
      products={products}
      searchTerms={searchTerms}
      categories={categories}
    />
  )
})

SearchContainer.displayName = 'SearchContainer'
